import { MethodMarks } from '../method-marks';
import { MarkedProxyDecorator } from '../proxy.decorator';

export interface ILogger {
  debug(message: string, input?: unknown): void;
  error(message: string, error: Error): void;
}

export interface CallLoggerOptions {
  /** Log the arguments each call was made with. */
  input: boolean;
  /** Log the value each call resolved to. */
  output: boolean;
}

/**
 * Per-method overrides, applied on top of the instance {@link CallLoggerOptions}.
 *
 * Derived with `Pick` so it cannot drift from the options it refines, and `Partial`
 * because either half may be overridden alone.
 */
export type LogOverrides = Partial<Pick<CallLoggerOptions, 'input' | 'output'>>;

const marks = new MethodMarks<LogOverrides>('shared-decorators:loggedMethods');

/**
 * Opts a method into {@link CallLogger}, and optionally refines what is logged.
 *
 * A gate, like {@link retryable} and {@link rateLimited}: an unmarked method is not
 * logged at all, however the logger was configured. Bare, the mark takes the instance
 * options as given; with overrides it narrows them per method.
 *
 * Only a class that can import this package can be logged, which excludes use-case
 * handlers in `layer:application` libs — log the controller instead, which is the layer
 * that owns delivery concerns anyway.
 *
 * @example
 * class S3FileRepository {
 *   @logged()                                   // logged, on the instance's terms
 *   public async list(path: string): Promise<string[]> { ... }
 *
 *   @logged({ input: false, output: false })     // logged, without the payloads
 *   public async write(path: string, content: Buffer): Promise<void> { ... }
 *
 *   public getAbsolutePath(name: string): string { ... }   // never logged
 * }
 */
export function logged(overrides?: LogOverrides): MethodDecorator {
  return (prototype, propertyKey) => marks.add(prototype, propertyKey, overrides);
}

/**
 * The logging overrides declared on `target` or up its prototype chain.
 *
 * A delegating function rather than `= marks.of`: detaching a method from its instance
 * loses `this`, which would compile and then fail at runtime.
 */
export function loggedMethodsOf(target: object): ReadonlyMap<string, LogOverrides | undefined> {
  return marks.of(target);
}

/**
 * CallLogger
 *
 * A proxy-based decorator that augments a class instance with observability features,
 * specifically logging method invocations and errors using the provided logger.
 *
 * When applied, all method calls on the target class are intercepted:
 *   - On successful completion (sync or async), a debug log is recorded with the method name, arguments, and result.
 *   - If an error occurs (sync throw or async rejection), an error log is recorded with the method name and error.
 *
 * Log lines are prefixed with the decorated target's class name, so one instance can
 * be reused across targets and the output still says where each call came from.
 *
 * **Compose it inside a {@link Retrier}**, so every attempt is logged and a retry storm is
 * diagnosable. {@link CallMeter} belongs on the other side — outside — where counting the
 * operation rather than its attempts keeps the metric meaningful.
 *
 * @example
 * const quietLogging = new CallLogger(logger, { input: false, output: false });
 * const repo = quietLogging.decorate(fileRepository);
 * const processor = quietLogging.decorate(imageProcessor);   // same instance, another target
 */
export class CallLogger extends MarkedProxyDecorator<LogOverrides> {
  constructor(
    private readonly logger: ILogger,
    private readonly options: CallLoggerOptions = { input: true, output: true }
  ) {
    super(marks);
  }

  protected wrapMarked(
    method: (...args: unknown[]) => unknown,
    name: string,
    override: LogOverrides | undefined,
    target: object
  ): (...args: unknown[]) => unknown {
    const className = target.constructor.name;
    const methodName = `[${className}] ${name}`;

    const input = override?.input ?? this.options.input;
    const output = override?.output ?? this.options.output;

    return (...args: unknown[]) => {
      try {
        if (input) this.logger.debug(`${methodName} called`, { args });
        else this.logger.debug(`${methodName} called`);

        const result = method(...args);

        if (result instanceof Promise) {
          return result
            .then((value) => {
              if (output) this.logger.debug(`${methodName} succeeded`, { result: value });
              else this.logger.debug(`${methodName} succeeded`);
              return value;
            })
            .catch((err: unknown) => {
              this.logger.error(`${methodName} failed`, err as Error);
              throw err;
            });
        }

        if (output) this.logger.debug(`${methodName} succeeded`, { result });
        else this.logger.debug(`${methodName} succeeded`);
        return result;
      } catch (err) {
        this.logger.error(`${methodName} failed`, err as Error);
        throw err;
      }
    };
  }
}
